import { useDispatch } from 'react-redux';
import { isUuid } from '@couchbasecloud/base-ui/utils/uuid';
import { createEstablishingContextErr } from 'error/context-error';
import { useOrganizationList } from 'hooks/use-organization-list/use-organization-list';
import { useStorage } from 'hooks/use-storage/use-storage';
import { useUrlManager } from 'hooks/use-url-manager/use-url-manager';
import { useAppSelector } from 'store/hooks';
import { reset, setOrganizationId } from 'store/organization.slice';
import { parseJWT } from 'sync/jwt-service/jwt-service';
import { User } from 'sync/user-service';
import { debugLog } from 'utils/debug';
import { onChangePendoOrganization } from 'utils/pendo';
import { queryClient, ReactQueryKeys } from 'utils/react-query';
import { validateOrganizationStorage } from './use-organization-manager.utils';

// TODO move to env file
const ORGANIZATION_STORAGE_KEY = 'lastOrg2211';

/**
 * Before use returned functions be sure that areOrganizationRecordsLoading is false
 */
export const useOrganizationManager = () => {
  const {
    createURL,
    currentIdentifiers: { organizationId: organizationIdFromUrl },
  } = useUrlManager();

  const {
    getById: getOrganizationById,
    mustGetFirstOrganizationIdAvailable,
    areOrganizationRecordsLoading,
    isOrganizationFetched,
  } = useOrganizationList({ fetchTrials: true });

  const {
    read: readStorageValue,
    remove: removeStorageValue,
    save: saveStorageValue,
  } = useStorage<string | null>({
    storageKey: ORGANIZATION_STORAGE_KEY,
    fallbackValue: null,
    shouldStoreAsJson: false,
    validate: validateOrganizationStorage,
  });

  const dispatch = useDispatch();
  const organizationId = useAppSelector((state) => state.organization.organizationId);
  const mappedOrganizationId = {
    organizationId: organizationId ?? undefined,
  };

  const resetOrganizationId = () => {
    debugLog('hooks/use-organization-manager - resetOrganizationId');
    dispatch(reset());
  };

  const changeOrganizationId = (userId: string, newOrganizationId: string | null) => {
    debugLog(`hooks/use-organization-manager - changeOrganizationId changing organizationId to ${newOrganizationId}`);

    dispatch(setOrganizationId(newOrganizationId));

    if (!newOrganizationId || !userId) {
      debugLog('hooks/use-organization-manager - removeStorageValue');
      removeStorageValue();
      return;
    }

    debugLog(`hooks/use-organization-manager persisting organizationId ${newOrganizationId}`);

    const organization = getOrganizationById(newOrganizationId);
    if (organization) {
      onChangePendoOrganization(organization);
    }
    saveStorageValue(`${userId}:${newOrganizationId}`);
  };

  const getOrganizationIdFromLocalStorage = (userId: string) => {
    const organizationStorage = readStorageValue();
    const [storedUserId, storedOrganizationId] = (organizationStorage ?? '').split(':');
    // ensure the returned value is associated with the active session.
    // Otherwise, people won't be able to sign in/out of multiple accounts
    // on the same machine.
    if (storedUserId !== userId) {
      debugLog(`hooks/use-organization-manager getOrganizationIdFromLocalStorage() returning stored id: refusing to pass on value`);
      resetOrganizationId();
      return null;
    }

    return storedOrganizationId;
  };

  const attemptUpdate = (id?: string | null) => {
    if (!id || !isUuid(id)) {
      return null;
    }
    const organization = getOrganizationById(id);

    if (organization) {
      dispatch(setOrganizationId(id));
    }
    return organization;
  };

  const initializeOrganization = () => {
    // we call this hooks in FlagsProvider component which is part of Module Federation
    // component so it can be called when the user is not logged in, THerefor
    // we use parseJWT and trying to obtain user from cache (queryClient) since we can't use useUser hook here
    const userId = parseJWT()?.sub ?? '';

    const organizationFromUrl = attemptUpdate(organizationIdFromUrl);

    if (organizationFromUrl) {
      debugLog(`hooks/use-organization-manager initializeOrganization() established context using requested id: ${organizationIdFromUrl}`);
      return organizationFromUrl;
    }

    // Second, try from the previously established context.
    const organizationFromStore = attemptUpdate(organizationId);
    if (organizationFromStore) {
      debugLog(`hooks/use-organization-manager initializeOrganization() established context from it's store: ${organizationId}`);
      return organizationFromStore;
    }

    // Third, try to recall the last organization the user used on this device
    const idFromLocalStorage = getOrganizationIdFromLocalStorage(userId);
    const organizationFromLocalStorage = attemptUpdate(idFromLocalStorage);
    if (organizationFromLocalStorage) {
      debugLog(`hooks/use-organization-manager initializeOrganization() established context from it's localStorage: ${idFromLocalStorage}`);
      return organizationFromLocalStorage;
    }

    // Fourth, try for lastAccessedTenant if it exists from sessions endpoint
    const sessionUser = queryClient.getQueryData<User>(ReactQueryKeys.USER({ userId }));
    const lastAccessedTenant = sessionUser?.lastAccessedTenant;
    const organizationFromSessionUser = attemptUpdate(lastAccessedTenant);
    if (organizationFromSessionUser) {
      debugLog(
        `hooks/use-organization-manager initializeOrganization() established context from it's last accessed tenant: ${organizationFromSessionUser}`
      );
      return organizationFromSessionUser;
    }

    // Fifth, settle for any valid organization the user has access to.
    const firstAvailableOrganizationId = mustGetFirstOrganizationIdAvailable();
    const availableOrganization = attemptUpdate(firstAvailableOrganizationId);
    if (availableOrganization) {
      debugLog(
        `hooks/use-organization-manager initializeOrganization() established context by random selection of available organizations: ${firstAvailableOrganizationId}`
      );
      return availableOrganization;
    }

    // We have an orphan. This is a bad experience but a perfectly valid one.
    // Organizations can remove users -- but those users still have
    // Capella accounts. These orphan users can be invited to join any
    // organization. This might be common for an agency, services firm,
    // or integrator. This is definitely a valid case for Couchbase Employees
    // who have access to multiple organizations and can have all of their
    // access revoked as they change teams.

    debugLog('hooks/use-organization-manager initializeOrganization() unable to initialize organizationId from any source');

    createEstablishingContextErr('Unable to load organization record.');
    return null;
  };

  const getHrefToOrganizationProjectsList = () => createURL('/projects', mappedOrganizationId);

  const getHrefToOrganizationDatabasesList = () => createURL('/databases', mappedOrganizationId);

  const getHrefToOrganizationAppServicesList = () => createURL('/app-services', mappedOrganizationId);

  return {
    changeOrganizationId,
    resetOrganizationId,
    initializeOrganization,
    organizationId,
    mappedOrganizationId,
    areOrganizationRecordsLoading,
    isOrganizationFetched,
    getHrefToOrganizationProjectsList,
    getHrefToOrganizationDatabasesList,
    getHrefToOrganizationAppServicesList,
    getDefaultOrganizationHref: getHrefToOrganizationDatabasesList,
  };
};
